import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";

const ITEMS = [
  { label: "Open Overview", to: "/", hint: "1" },
  { label: "Open Month Grid", to: "/month", hint: "2" },
  { label: "Open Weekly OS", to: "/weekly", hint: "3" },
  { label: "Open Time Board", to: "/board", hint: "4" },
  { label: "Open Deal Pipeline", to: "/deals", hint: "5" },
  { label: "Open Real Estate Media", to: "/media", hint: "6" },
  { label: "Open Calendar Sync", to: "/calendar", hint: "7" },
  { label: "Open Weekly Briefing", to: "/briefing", hint: "8" },
  { label: "Open Settings", to: "/settings", hint: "9" },
];

export default function CommandPalette({ open, onClose }) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setQ("");
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const filtered = ITEMS.filter((i) =>
    i.label.toLowerCase().includes(q.toLowerCase())
  );

  useEffect(() => setActive(0), [q]);

  if (!open) return null;

  const onKey = (e) => {
    if (e.key === "Escape") {
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(filtered.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const it = filtered[active];
      if (it) {
        navigate(it.to);
        onClose();
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[18vh] px-4 bg-black/55 backdrop-blur-sm animate-fadein"
      onClick={onClose}
      data-testid="command-palette"
    >
      <div
        className="w-full max-w-xl hud-frame"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "hsl(var(--popover))" }}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <Search size={14} className="text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Jump to anywhere…"
            className="flex-1 bg-transparent outline-none text-sm font-sans"
            data-testid="command-input"
          />
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            data-testid="command-close"
          >
            <X size={14} />
          </button>
        </div>
        <ul className="py-2 max-h-72 overflow-y-auto">
          {filtered.map((it, i) => (
            <li
              key={it.to}
              onMouseEnter={() => setActive(i)}
              onClick={() => {
                navigate(it.to);
                onClose();
              }}
              data-active={i === active}
              data-testid={`command-item-${it.to.replace("/", "") || "home"}`}
              className={`flex items-center justify-between px-4 py-2.5 cursor-pointer text-sm ${
                i === active ? "bg-[hsl(var(--oxblood)/0.12)]" : ""
              }`}
            >
              <span>{it.label}</span>
              <span className="mono text-[10px] text-muted-foreground">
                ⇧ {it.hint}
              </span>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="px-4 py-6 text-center text-muted-foreground text-sm">
              No matches.
            </li>
          )}
        </ul>
        <div className="px-4 py-2 border-t border-border flex items-center justify-between mono text-[10px] text-muted-foreground uppercase tracking-widest">
          <span>↑ ↓ navigate</span>
          <span>↵ select · esc close</span>
        </div>
      </div>
    </div>
  );
}
